import React from "react"
import { Dispatch } from "redux"

import { DESC } from "const"
import { QidModelOption } from "server/models"
import { sort } from "client/store/utils"
import {
    ResultDatapointListType,
    SilModelDevicesQidExpanded,
    CalculateQidResultProps,
    CalculateQidResultData,
    setSilModelDevicesQidExpanded,
} from "client/store"
import {
    TableRow,
    TableCell,
    TableCellContentWrapper,
    TableCellContent,
    Empty,
    NoDatapoints,
} from "client/components"

import {
    TableCellLined,
    RowCollapseControl,
} from "client/pages/qid/product/models/list/styled"

import { ColumnsType, SelectDeviceType, SelectDevice } from "../columns"
import Datapoint from "../nestedDatapoints"

import { paddingStyle } from "./index"

const OptionRows = (
    columns: ColumnsType,
    options: readonly QidModelOption[],
    expanded: SilModelDevicesQidExpanded,
    modelId: number,
    safetyFunctionId: number,
    lastModelRow: boolean,
    lastFunctionRow: boolean,
    baseCalculationProps: CalculateQidResultProps,
    selectFunctionProps: SelectDeviceType,
    datapointList: ResultDatapointListType,
    dispatch: Dispatch,
) => {
    const toggleOption = (optionId: number) => {
        const expandedOptions = expanded.optionIds.slice()
        if (expandedOptions.includes(optionId)) {
            dispatch(setSilModelDevicesQidExpanded({
                optionIds: expandedOptions.filter((id) => id !== optionId),
            }))
        } else {
            expandedOptions.push(optionId)
            dispatch(setSilModelDevicesQidExpanded({ optionIds: expandedOptions }))
        }
    }

    const rows: React.ReactNode[] = []

    if (!options.length) {
        rows.push(
            <TableRow key={`emptySafetyFunction_${safetyFunctionId}`} size="sm">
                <TableCellLined lineThrough={!lastModelRow} />
                <TableCellLined lineThrough={!lastFunctionRow} />
                <TableCellLined lineHalfCross />
                <TableCell colSpan={100}>
                    <TableCellContentWrapper>
                        <Empty />
                    </TableCellContentWrapper>
                </TableCell>
            </TableRow>,
        )
    }

    options.forEach((option, optionIndex) => {
        const optionExpanded = expanded.optionIds.includes(option.id)
        const lastOptionRow = options.length - 1 === optionIndex
        const optionAvg = CalculateQidResultData({
            ...baseCalculationProps,
            modelId,
            safetyFunctionId,
            optionId: option.id,
        })

        const selectOptionProps: SelectDeviceType = {
            ...selectFunctionProps,
            option,
            dataAvg: optionAvg,
        }

        rows.push(
            <TableRow
                key={`option_${option.id}`}
                size="sm"
                onClick={() => SelectDevice(selectOptionProps)}
            >
                <TableCellLined lineThrough={!lastModelRow} />
                <TableCellLined lineThrough={!lastFunctionRow} />
                <TableCellLined
                    // all rows have "cross line", and the last row has only "half line"
                    lineCross={!lastOptionRow}
                    lineHalfCross={lastOptionRow}
                />
                <TableCell colSpan={columns.commonColumns.length + 1}>
                    <TableCellContentWrapper style={paddingStyle}>
                        <TableCellContent>
                            <RowCollapseControl
                                collapsed={!optionExpanded}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    toggleOption(option.id)
                                }}
                            />
                            {option.name}
                        </TableCellContent>
                    </TableCellContentWrapper>
                </TableCell>
                {
                    columns.resultColumns.map((column) => (
                        <TableCell
                            key={column.field}
                            cellWidth={column.cellWidth}
                            cellMinWidth={column.cellMinWidth}
                            cellMaxWidth={column.cellMaxWidth}
                        >
                            <TableCellContentWrapper>
                                <TableCellContent>
                                    {column.formatter(optionAvg)}
                                </TableCellContent>
                            </TableCellContentWrapper>
                        </TableCell>
                    ))
                }
            </TableRow>,
        )

        if (optionExpanded) {
            // order datapoints by "createdAt" DESC - show the newest on top
            const optionKey = `model_${modelId}_safetyFunction_${safetyFunctionId}_option_${option.id}`
            const optionDatapoints = datapointList[optionKey] && datapointList[optionKey].datapoints
                ? datapointList[optionKey].datapoints.slice().sort((a, b) => sort(a, b, "createdAt", DESC))
                : []

            rows.push(
                <TableRow key={`option_${option.id}_datapoints`} size="fit">
                    <TableCellLined lineThrough={!lastModelRow} />
                    <TableCellLined lineThrough={!lastFunctionRow} />
                    <TableCellLined lineThrough={!lastOptionRow} />
                    <TableCell colSpan={100}>
                        <TableCellContentWrapper columnDirection>
                            {
                                optionDatapoints.length === 0
                                && <NoDatapoints />
                            }
                            {
                                optionDatapoints.map((datapoint) => {
                                    const datapointAvg = CalculateQidResultData({
                                        ...baseCalculationProps,
                                        modelId,
                                        safetyFunctionId,
                                        optionId: option.id,
                                        datapointId: datapoint.id,
                                    })

                                    const selectOptionDatapointProps: SelectDeviceType = {
                                        ...selectOptionProps,
                                        method: null,
                                        datapoint,
                                        dataAvg: datapointAvg,
                                    }

                                    return (
                                        <Datapoint
                                            key={datapoint.id}
                                            datapoint={datapoint}
                                            datapointAvg={datapointAvg}
                                            onClick={() => SelectDevice(selectOptionDatapointProps)}
                                        />
                                    )
                                })
                            }
                        </TableCellContentWrapper>
                    </TableCell>
                </TableRow>,
            )
        }
    })

    return rows
}
export default OptionRows
